<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <!-- Loading State -->
    <v-layout
      v-if="isLoadingVerificationStatus"
      column
      align-center
      justify-center
      class="mt-5"
    >
      <v-progress-circular indeterminate color="primary" />
      <p class="mt-3 grey--text">Loading...</p>
    </v-layout>

    <!-- Email Verification Dialog -->
    <v-dialog v-model="showEmailVerification" persistent max-width="420">
      <EmailVerificationForm
        :token="token"
        :tax-return-id="taxReturnId"
        :partner-email="partnerEmail"
        :initial-locked="isLockedOut"
        :initial-code-requested="codeAlreadyRequested"
        @verified="handleVerificationSuccess"
      />
    </v-dialog>

    <!-- Consent Dialog -->
    <v-dialog
      v-model="showConsentDialog"
      persistent
      max-width="500"
      v-if="!bypassConsent && !showEmailVerification"
    >
      <v-card>
        <v-card-title class="title">
          Electronic K-1 Package Delivery Consent
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 text-xs-left">
          <!-- Expired Token Content -->
          <div v-if="tokenExpired">
            <p><b>Link Expired</b></p>
            <p>
              The link for this K-1 package has expired. Please request a new
              one to be sent to your email.
            </p>
          </div>
          <!-- Normal Consent Content -->
          <div v-else>
            <p>
              You are about to receive your K-1 package electronically. By
              clicking "I Consent", you consent to receive this tax document
              electronically. The link will expire in 7 days.
            </p>
            <p class="mt-3">
              <strong>Important:</strong> You have the right to decline
              electronic delivery and receive your K-1 package physically or by
              mail instead. By clicking "Decline", the managing partner or
              shareholder will be notified and will deliver the K-1 package to
              you.
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <!-- Expired Token Actions -->
          <template v-if="tokenExpired">
            <v-btn
              class="text-none primary"
              @click="resendEmail"
              :loading="resendLoading"
              :disabled="resendLoading"
            >
              Resend Email
            </v-btn>
          </template>
          <!-- Normal Consent Actions -->
          <template v-else>
            <v-btn
              class="text-none error--text"
              flat
              @click="handleDecline"
              :loading="declineLoading"
              :disabled="declineLoading"
            >
              Decline
            </v-btn>
            <v-btn
              color="primary"
              class="text-none"
              @click="handleConsent"
              :loading="consentLoading"
              :disabled="consentLoading"
            >
              I Consent
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Decline Dialog -->
    <v-dialog v-model="showDeclineDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="title">
          K-1 Package Delivery
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 text-xs-left">
          <p>
            You have declined electronic delivery of your K-1 package.
          </p>
          <p class="mt-3">
            Your managing partner or shareholder will deliver your K-1 package
            physically or by mail. You may now close this page.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Main Content (shown after consent or when bypassed) -->
    <div
      v-if="
        (consentGiven || bypassConsent) &&
          !showConsentDialog &&
          !showDeclineDialog
      "
    >
      <!-- Partial: Alert -->
      <sc-alert type="info" class="sticky" v-if="!tokenExpired">
        <div v-if="bypassConsent">
          <p><b>K-1 Package for Physical Delivery</b></p>
          <p>
            This K-1 package was declined for electronic delivery. Please
            download and deliver it physically or by mail to the partner or
            member.
          </p>
        </div>
        <div v-else>
          Please scroll down to <b>review</b> the K-1 package. After reviewing,
          click the <b>Download</b> button to save a copy.
        </div>

        <sc-button-group slot="button-group">
          <sc-button
            type="ghost"
            icon-left="download"
            @click="downloadPdf"
            data-test-id="download-k1-button"
          >
            Download
          </sc-button>
        </sc-button-group>
      </sc-alert>

      <!-- Expired Token Alert -->
      <sc-alert type="error" class="sticky" v-if="tokenExpired">
        <div>
          <p><b>Download Link Expired</b></p>
          <p>
            The download link for this K-1 package has expired. Please request a
            new one to be sent to your email.
          </p>
        </div>

        <sc-button-group slot="button-group">
          <sc-button
            type="primary"
            icon-left="email"
            @click="resendEmail"
            :loading="resendLoading"
            :disabled="resendLoading"
          >
            Resend Email
          </sc-button>
        </sc-button-group>
      </sc-alert>

      <!-- Partial: Content -->
      <sc-flex :is-column="true">
        <!-- State: Loading -->
        <sc-flex
          v-if="pdfLoading"
          :is-centered="true"
          :is-column="true"
          :is-full-width="true"
        >
          <br />
          <v-progress-circular indeterminate color="primary" />
        </sc-flex>
        <!-- State: Partial Pages -->
        <section class="pdf-page-container" v-else-if="!tokenExpired">
          <div v-for="pageImage in pageImages">
            <img :src="pageImage.thumbnail" class="pdf" />
          </div>
        </section>
      </sc-flex>
    </div>
  </sc-flex>
</template>

<script>
import API from '@/api';
import { PDFDocument } from 'pdf-lib';
import { generatePdfThumbnails } from '@/utils/pdf';
import UtilsMixins from '@/mixins/UtilsMixins';
import EmailVerificationForm from './components/EmailVerificationForm';

export default {
  components: {
    EmailVerificationForm,
  },

  mixins: [UtilsMixins],

  data() {
    return {
      // Token and ID
      token: null,
      taxReturnId: null,
      partnerEmail: null,

      // Verification state
      isLoadingVerificationStatus: true,
      verificationEnabled: false,
      verificationRequired: false,
      showEmailVerification: false,
      bypassVerification: false,
      isLockedOut: false,
      codeAlreadyRequested: false,

      // Consent state
      showConsentDialog: false,
      showDeclineDialog: false,
      consentGiven: false,
      consentLoading: false,
      declineLoading: false,
      bypassConsent: false,

      // PDF state
      pdfSrc: null,
      pdfLoading: false,
      pdfDoc: null,
      pdfData: null,
      pageImages: [],
      tokenExpired: false,
      resendLoading: false,
    };
  },

  methods: {
    handleVerificationError(error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.error === 'verification_locked'
      ) {
        this.showConsentDialog = false;
        this.$store.dispatch('application/setErrorNotification', {
          message:
            error.response.data.message ||
            'This K-1 package has been locked due to too many failed verification attempts. Please contact the sender.',
        });
        return true;
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error === 'verification_required'
      ) {
        // Redirect back to verification
        this.showEmailVerification = true;
        this.showConsentDialog = false;
        this.$store.dispatch('application/setErrorNotification', {
          message:
            error.response.data.message || 'Please verify your identity first.',
        });
        return true;
      }
      return false;
    },
    async handleConsent() {
      const token = this.$route.query.token;

      this.consentLoading = true;

      try {
        await API.taxReturn.updateK1ElectronicConsent({
          token,
          consented: true,
          taxReturnId: this.taxReturnId,
          partnerEmail: this.partnerEmail,
        });

        this.consentGiven = true;
        this.showConsentDialog = false;

        // Load the K-1 package after consent
        await this.loadK1Package();
      } catch (error) {
        console.error('Error updating consent:', error);

        // Check if it's an expired token error
        if (
          error.response &&
          error.response.data &&
          error.response.data.error === 'expired_token'
        ) {
          this.tokenExpired = true;
          this.$store.dispatch('application/setErrorNotification', {
            message: error.response.data.message || 'The link has expired.',
          });
        } else if (!this.handleVerificationError(error)) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Failed to process your consent. Please try again.',
          });
        }
      } finally {
        this.consentLoading = false;
      }
    },

    async handleDecline() {
      const token = this.$route.query.token;

      this.declineLoading = true;

      try {
        await API.taxReturn.updateK1ElectronicConsent({
          token,
          consented: false,
          taxReturnId: this.taxReturnId,
          partnerEmail: this.partnerEmail,
        });

        this.showConsentDialog = false;
        this.showDeclineDialog = true;
      } catch (error) {
        console.error('Error updating consent:', error);

        // Check if it's an expired token error
        if (
          error.response &&
          error.response.data &&
          error.response.data.error === 'expired_token'
        ) {
          this.tokenExpired = true;
          this.$store.dispatch('application/setErrorNotification', {
            message: error.response.data.message || 'The link has expired.',
          });
        } else if (this.handleVerificationError(error)) {
          // Handled by helper
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Failed to process your request. Please try again.',
          });
        }
      } finally {
        this.declineLoading = false;
      }
    },

    async loadK1Package() {
      try {
        this.pdfLoading = true;

        await this.getK1PackageDownloadLink();

        if (!this.tokenExpired) {
          const pdfBytes = await fetch(this.pdfSrc).then(res =>
            res.arrayBuffer(),
          );

          this.pdfDoc = await PDFDocument.load(pdfBytes);
          this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });
          this.pageImages = await generatePdfThumbnails(this.pdfData, 1000);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.pdfLoading = false;
      }
    },

    async getK1PackageDownloadLink() {
      const token = this.$route.query.token;

      try {
        const response = await API.taxReturn.getK1PackageDownloadLink({
          token,
          taxReturnId: this.taxReturnId,
          partnerEmail: this.partnerEmail,
        });

        this.pdfSrc = response.data.link;
        this.tokenExpired = false;
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.error === 'expired_token'
        ) {
          this.tokenExpired = true;
          this.$store.dispatch('application/setErrorNotification', {
            message: error.response.data.message,
          });
        } else if (this.handleVerificationError(error)) {
          // Handled by helper
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'An error occurred while loading the K-1 package.',
          });
        }
        throw error;
      }
    },

    async resendEmail() {
      const token = this.$route.query.token;

      this.resendLoading = true;

      try {
        await API.taxReturn.resendK1PackageEmail({
          token,
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'K-1 package email has been resent successfully. Please check your email for the new link.',
        });

        window.close();
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'Failed to resend the K-1 package email. Please contact your tax preparer.',
        });
      } finally {
        this.resendLoading = false;
      }
    },

    async downloadPdf() {
      await this.downloadDocumentByLink(this.pdfSrc, 'K1_Package');
    },

    async checkVerificationStatus() {
      try {
        const response = await API.taxReturn.checkK1Verification({
          taxReturnId: this.taxReturnId,
          token: this.token,
          partnerEmail: this.partnerEmail,
        });

        const data = response.data;

        this.verificationEnabled = data.verification_enabled;
        this.bypassVerification = data.bypass_verification;
        this.partnerEmail = data.partner_email;
        this.isLockedOut = data.locked || false;

        // Only consider code_requested if backend says user is verified
        // Backend validates any verification token we sent in the request
        this.codeAlreadyRequested = data.verified
          ? data.code_requested || false
          : false;

        // Determine if verification is needed
        // Note: We rely on backend's 'verified' status (which validates any token we sent)
        // Don't check storedToken here - backend already validated it if we sent it
        if (
          !data.verification_enabled ||
          data.bypass_verification ||
          data.verified
        ) {
          // No verification needed - proceed to consent
          this.verificationRequired = false;
          this.showEmailVerification = false;
          this.proceedToConsent();
        } else {
          // Verification required - always show email verification
          this.verificationRequired = true;
          this.showEmailVerification = true;
        }
      } catch (error) {
        // Handle expired token separately - don't proceed
        if (
          error.response &&
          error.response.data &&
          error.response.data.error === 'expired_token'
        ) {
          this.tokenExpired = true;
          this.$store.dispatch('application/setErrorNotification', {
            message: error.response.data.message || 'The link has expired.',
          });
        } else if (
          error.response &&
          (error.response.status === 404 || error.response.status === 501)
        ) {
          // Feature not available on backend (404) or not implemented (501)
          // Fall back to original flow for backward compatibility
          this.verificationEnabled = false;
          this.proceedToConsent();
        } else {
          // Real error (5xx, network failure, etc.) - show error to user
          // Do NOT silently bypass security on server errors
          const errorMessage =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Unable to verify access. Please try again or contact support.';
          this.$store.dispatch('application/setErrorNotification', {
            message: errorMessage,
          });
        }
      } finally {
        this.isLoadingVerificationStatus = false;
      }
    },

    handleVerificationSuccess() {
      // Verification successful - proceed to consent dialog
      this.showEmailVerification = false;
      this.proceedToConsent();
    },

    proceedToConsent() {
      // Check if consent should be bypassed
      this.checkBypassConsent();

      // If not bypassed, show consent dialog
      if (!this.bypassConsent) {
        this.showConsentDialog = true;
      }
    },

    // Check if consent should be bypassed based on JWT token
    checkBypassConsent() {
      if (!this.token) return;

      try {
        // Decode the JWT token to check for bypass_consent
        const payload = JSON.parse(atob(this.token.split('.')[1]));
        if (payload.bypass_consent === true) {
          this.bypassConsent = true;
          this.showConsentDialog = false;
          // Load the K-1 package immediately when bypassed
          this.loadK1Package();
        }
      } catch (error) {
        console.error('Error decoding JWT token:', error);
      }
    },

    decodeToken() {
      // Extract token from query params
      this.token = this.$route.query.token;
      if (!this.token) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Invalid or missing token.',
        });
        return false;
      }

      try {
        // Decode the JWT token to get tax_return_id
        const payload = JSON.parse(atob(this.token.split('.')[1]));

        this.taxReturnId = payload.tax_return_id;
        this.partnerEmail = payload.partner_email;

        if (!this.taxReturnId) {
          throw new Error('Missing tax_return_id in token');
        }

        return true;
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Invalid token format.',
        });
        return false;
      }
    },
  },

  async created() {
    // Decode token to get IDs
    if (!this.decodeToken()) {
      this.isLoadingVerificationStatus = false;
      return;
    }

    // Check verification status first
    await this.checkVerificationStatus();
  },
};
</script>

<style scoped>
.sticky {
  position: sticky;
  top: 72px;
  z-index: 2;
}
</style>
